/**
 * Public client entry: /m/{slug} — fără cont Firebase; date din publicSites/{slug}.
 * Boot minimal: un singur fetch, fără listener înainte de primul render; hydrate după P10.
 */
import { db } from '../config/firebase.js?v=82.86';
import {
  setCurrentSettings,
  validateSettingsFields,
  migratePrizesToHaveIds,
  startPublicSiteListener,
  stopPublicSiteListener
} from './settings.js?v=82.86';
import { logger } from '../utils/logger.js?v=82.86';
import { validatePublicSiteDocumentForClient } from '../utils/public-site-validation.js?v=82.86';
import { withTimeoutMs } from '../utils/with-timeout.js?v=82.86';
import {
  publicBootTimeline,
  hasPublicStaticTestParam
} from '../utils/public-boot-timeline.js?v=82.86';
import {
  applyPublicBootstrapLogo,
  applyClientPublicBrandLogo,
  initEarlyPublicBootstrapFromPath,
  setPublicBootstrapTitle,
  setPublicBootstrapLoadingHint
} from '../utils/public-bootstrap-branding.js?v=82.86';

function sjPublicLog(level, ...args) {
  try {
    const fn = console[level] || console.log;
    fn.call(console, '[SJ-Public]', ...args);
  } catch (_) {}
}

let activePublicSlug = null;

let _bootPct = 0;
function updateBootProgress(pct) {
  _bootPct = Math.max(_bootPct, Math.min(pct, 100));
  const fill = document.getElementById('sjBootProgressFill');
  const pctEl = document.getElementById('sjBootProgressPct');
  if (fill) fill.style.width = _bootPct + '%';
  if (pctEl) pctEl.textContent = _bootPct + '%';
}

let injectedShowPage = null;
/** Bandă discretă pe /m/{slug} pentru demo-urile interne de prospectare. */
function mountPublicInternalDemoRibbon() {
  try {
    if (document.getElementById('sjPublicDemoRibbon')) return;
    const r = document.createElement('div');
    r.id = 'sjPublicDemoRibbon';
    r.textContent = 'Exemplu demonstrativ SpressJoy · neoficial';
    r.style.cssText =
      'position:fixed;top:0;left:0;right:0;z-index:99999;text-align:center;padding:4px 8px;' +
      'background:rgba(20,12,8,.82);color:#ffc878;font:600 12px/1.4 system-ui,sans-serif;' +
      'letter-spacing:.02em;pointer-events:none;';
    document.body.appendChild(r);
  } catch (_) {}
}

export function registerPublicClientShowPage(fn) {
  if (typeof fn === 'function') injectedShowPage = fn;
}

export function nudgeFirestoreAfterReconnect() {
  try {
    if (db && typeof db.enableNetwork === 'function') {
      return db.enableNetwork().catch(() => {});
    }
  } catch (_) {}
  return Promise.resolve();
}

function getShowPageSync() {
  if (injectedShowPage) return injectedShowPage;
  if (typeof window !== 'undefined' && typeof window.showPage === 'function') return window.showPage;
  return null;
}

async function resolveShowPageWithTimeout(ms) {
  const sync = getShowPageSync();
  if (sync) return sync;
  return withTimeoutMs(
    import('../ui/routing.js?v=82.86').then((m) => m.showPage),
    ms
  );
}

const PUBLIC_SLUG_RE = /^\/m\/([a-z0-9-]{3,48})\/?$/i;
const PUBLIC_SLUG_HASH_RE = /^#(?:\/)?m\/([a-z0-9-]{3,48})\/?$/i;

export function parsePublicSlugFromPath() {
  if (typeof window === 'undefined') return null;
  try {
    const path = window.location.pathname || '';
    const fromPath = path.match(PUBLIC_SLUG_RE);
    if (fromPath) return fromPath[1].toLowerCase();
    const hash = window.location.hash || '';
    const fromHash = hash.match(PUBLIC_SLUG_HASH_RE);
    if (fromHash) return fromHash[1].toLowerCase();
    return null;
  } catch {
    return null;
  }
}

export function getPublicClientSlug() {
  return activePublicSlug;
}

export function isPublicClientEntry() {
  return !!parsePublicSlugFromPath();
}

export function normalizePublicSlugClient(raw) {
  if (raw == null) return '';
  const s = String(raw).trim().toLowerCase();
  if (s === '') return '';
  if (s.length < 3 || s.length > 48) return null;
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(s)) return null;
  return s;
}

const PUBLIC_BOOTSTRAP_TIMEOUT_MS = 15000;
const RESOLVE_SHOWPAGE_TIMEOUT_MS = 2000;
const PUBLIC_SITE_GET_SERVER_MS = 9000;
const PUBLIC_SITE_GET_FALLBACK_MS = 4000;
/** Dacă SDK-ul Firestore nu răspunde în atâta timp, pornim în paralel citirea prin HTTPS simplu (REST). */
const PUBLIC_SITE_REST_AFTER_MS = 2500;
/** Logo-ul de pe ecranul de încărcare nu are voie să țină pagina în loc. */
const PUBLIC_LOGO_MAX_WAIT_MS = 1500;

/* ---- Citire prin REST (rezervă) ----
   Pe unele rețele / telefoane (VPN, DNS privat, Wi-Fi de firmă, conexiune care „se agață”), conexiunea
   specială a SDK-ului Firestore nu răspunde, deși o cerere web obișnuită trece. Documentul publicSites/{slug}
   e public la citire (regulile permit get), deci îl putem lua și cu fetch(). */
function decodeRestValue(v) {
  if (!v || typeof v !== 'object') return null;
  if ('stringValue' in v) return v.stringValue;
  if ('integerValue' in v) return Number(v.integerValue);
  if ('doubleValue' in v) return Number(v.doubleValue);
  if ('booleanValue' in v) return !!v.booleanValue;
  if ('nullValue' in v) return null;
  if ('timestampValue' in v) return v.timestampValue;
  if ('referenceValue' in v) return v.referenceValue;
  if ('geoPointValue' in v) return v.geoPointValue;
  if ('bytesValue' in v) return v.bytesValue;
  if ('arrayValue' in v) return ((v.arrayValue && v.arrayValue.values) || []).map(decodeRestValue);
  if ('mapValue' in v) return decodeRestFields((v.mapValue && v.mapValue.fields) || {});
  return null;
}
function decodeRestFields(fields) {
  const out = {};
  Object.keys(fields || {}).forEach((k) => { out[k] = decodeRestValue(fields[k]); });
  return out;
}
async function fetchPublicSiteViaRest(slug, signal) {
  const projectId = (db && db.app && db.app.options && db.app.options.projectId) || 'spressjoy';
  const url = `https://firestore.googleapis.com/v1/projects/${encodeURIComponent(projectId)}/databases/(default)/documents/publicSites/${encodeURIComponent(slug)}`;
  const res = await fetch(url, { cache: 'no-store', signal });
  if (res.status === 404) return { exists: false, data: () => undefined, viaRest: true };
  if (!res.ok) throw new Error(`REST ${res.status}`);
  const json = await res.json();
  const data = decodeRestFields(json.fields || {});
  return { exists: true, data: () => data, viaRest: true };
}

/** SDK-ul (server-first, apoi get implicit) în cursă cu REST-ul pornit după PUBLIC_SITE_REST_AFTER_MS. */
function fetchPublicSiteDoc(ref, slug) {
  return new Promise((resolve, reject) => {
    let settled = false;
    let sdkFailed = false;
    let restFailed = false;
    let restStarted = false;
    let lastErr = null;
    const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const win = (snap, how) => {
      if (settled) return;
      settled = true;
      publicBootTimeline('[P7]', `public doc via ${how} exists=${!!(snap && snap.exists)}`);
      if (how !== 'rest' && ctrl) { try { ctrl.abort(); } catch (_) { /* ignore */ } }
      resolve(snap);
    };
    const maybeFail = () => {
      if (settled) return;
      if (sdkFailed && (restFailed || !restStarted)) {
        if (!restStarted) { startRest(); return; }
        settled = true;
        reject(lastErr || new Error('public doc fetch failed'));
      }
    };
    const startRest = () => {
      if (restStarted || settled) return;
      restStarted = true;
      publicBootTimeline('[P6r]', 'REST fallback started');
      fetchPublicSiteViaRest(slug, ctrl && ctrl.signal)
        .then((snap) => win(snap, 'rest'))
        .catch((e) => { restFailed = true; lastErr = lastErr || e; sjPublicLog('warn', 'REST fetch failed', String(e && e.message)); maybeFail(); });
    };
    const restTimer = window.setTimeout(startRest, PUBLIC_SITE_REST_AFTER_MS);
    (async () => {
      try {
        const snap = await withTimeoutMs(ref.get({ source: 'server' }), PUBLIC_SITE_GET_SERVER_MS);
        window.clearTimeout(restTimer);
        win(snap, 'sdk-server');
      } catch (e) {
        publicBootTimeline('[E3]', `server fetch timeout/fail: ${e && e.message}`);
        sjPublicLog('warn', 'server fetch timeout or fail', String(e && e.message));
        startRest();
        try {
          const snap2 = await withTimeoutMs(ref.get(), PUBLIC_SITE_GET_FALLBACK_MS);
          win(snap2, 'sdk-default');
        } catch (e2) {
          sdkFailed = true;
          lastErr = e2;
          maybeFail();
        }
      }
    })();
  });
}

function schedulePublicPostBoot(slug, onStatusChange) {
  const run = () => {
    try {
      publicBootTimeline('[P10b]', 'startPublicSiteListener (post-render)');
      startPublicSiteListener(slug, onStatusChange);
    } catch (e) {
      publicBootTimeline('[E-listener]', String(e && e.message));
    }
    import('../ui/slideshow.js?v=82.86')
      .then((m) => {
        publicBootTimeline('[P10c]', 'full applyClientUI (imagini, promo)');
        m.applyClientUI();
        // Link public la documente: /m/{slug}?doc=regulament | ?doc=gdpr
        // Câștigătorii publicați (tombolă): banner discret
        Promise.all([import('../legal/legal-ui.js?v=82.86'), import('./settings.js?v=82.86')])
          .then(([lg, st]) => lg.mountWinnersBanner(st.getCurrentSettings() || {}))
          .catch(() => {});
        if (/[?&]oferta=/.test(location.search)) {
          import('../ui/client-promo-gallery.js?v=82.86').then((g) => g.openPromoFromUrl()).catch(() => {});
        }
        if (/[?&]doc=/.test(location.search)) {
          Promise.all([import('../legal/legal-ui.js?v=82.86'), import('./settings.js?v=82.86')])
            .then(([lg, st]) => lg.openDocFromUrl(st.getCurrentSettings() || {}))
            .catch(() => {});
        }
      })
      .catch((e) => {
        publicBootTimeline('[E-hydrate]', String(e && e.message));
      });
  };
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => window.setTimeout(run, 0));
    });
  } else {
    window.setTimeout(run, 0);
  }
}

export async function bootstrapPublicClient(onStatusChange) {
  const slug = parsePublicSlugFromPath();
  if (!slug) return;
  if (window.__SJ_DESK_FRAME) return; // pe laptop pagina rulează în iframe-ul „telefon”

  publicBootTimeline('[P2]', 'route recognized as /m/{slug}');
  publicBootTimeline('[P3]', `slug extracted: ${slug}`);

  activePublicSlug = slug;
  window.__SJ_PUBLIC_BOOTSTRAP__ = true;

  initEarlyPublicBootstrapFromPath();
  setPublicBootstrapLoadingHint('Se deschide…');

  let bootstrapDone = false;
  let timeoutId = null;
  let trickleId = null;
  const startTrickle = () => {
    trickleId = window.setInterval(() => {
      if (_bootPct < 90) updateBootProgress(_bootPct + 1);
    }, 600);
  };
  const stopTrickle = () => {
    if (trickleId != null) { window.clearInterval(trickleId); trickleId = null; }
  };

  updateBootProgress(5);
  startTrickle();
  const clearBootstrapTimeout = () => {
    if (timeoutId != null) {
      window.clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  const bootstrap = document.getElementById('sjPublicBootstrap');

  const finishBootstrap = () => {
    if (bootstrapDone) return;
    bootstrapDone = true;
    stopTrickle();
    clearBootstrapTimeout();
    if (bootstrap) bootstrap.setAttribute('aria-busy', 'false');
    document.documentElement.classList.remove('sj-public-bootstrap-active');
    document.body.classList.remove('sj-public-bootstrap-active');
    document.body.classList.add('sj-public-ui-ready');
    publicBootTimeline('[P10]', 'sj-public-ui-ready set');
    // pe laptop: fundalul din jurul „telefonului” = prima poză a localului
    try {
      if (window.parent !== window && typeof window.parent.__sjDeskSetBg === 'function') {
        import('./settings.js?v=82.86')
          .then((st) => {
            const cs = st.getCurrentSettings() || {};
            const first = (Array.isArray(cs.bgURLs) && cs.bgURLs.find(Boolean)) || cs.bgURL || '';
            if (first) window.parent.__sjDeskSetBg(String(first));
          })
          .catch(() => {});
      }
    } catch (_) { /* alt origin / fără părinte */ }
    try {
      window.__SJ_PUBLIC_BOOTSTRAP__ = false;
      sessionStorage.removeItem('sj_public_online_resume_count');
    } catch (_) {}
    sjPublicLog('info', 'bootstrap finished — UI ready');
    try {
      if (typeof console !== 'undefined' && console.info) {
        console.info('[BOOT]', 'app ready');
      }
    } catch (_) {}
    window.setTimeout(() => {
      if (bootstrap) bootstrap.style.display = 'none';
    }, 320);
  };

  const triggerFullRecoveryOrReload = (reason) => {
    sjPublicLog('warn', 'auto recovery triggered', reason);
    publicBootTimeline('[E-recovery]', String(reason));
    try {
      if (typeof window.__SJ_runFullPublicRecovery === 'function') {
        window.__SJ_runFullPublicRecovery(reason);
        return;
      }
    } catch (e) {
      sjPublicLog('warn', 'full recovery threw', e);
    }
    window.location.reload();
  };

  timeoutId = window.setTimeout(async () => {
    if (bootstrapDone) return;
    publicBootTimeline('[E-watchdog]', 'bootstrap overall timeout');
    sjPublicLog('warn', 'bootstrap watchdog fired (timeout ms)', PUBLIC_BOOTSTRAP_TIMEOUT_MS);
    let sp = getShowPageSync();
    if (!sp) {
      try {
        sp = await resolveShowPageWithTimeout(RESOLVE_SHOWPAGE_TIMEOUT_MS);
        sjPublicLog('info', 'showPage resolved in watchdog');
      } catch (e) {
        publicBootTimeline('[E6]', `watchdog showPage: ${e && e.message}`);
        sjPublicLog('warn', 'showPage resolve failed in watchdog', e);
        triggerFullRecoveryOrReload('bootstrap-timeout-no-showPage');
        return;
      }
    }
    try {
      const errEl = document.getElementById('publicJoinErrorText');
      if (errEl) {
        errEl.textContent =
          'Nu am putut încărca pagina locației la timp. Poți încerca din nou sau reîncărcare forțată mai jos.';
      }
      await sp('publicJoinErrorPage');
      finishBootstrap();
    } catch (e) {
      sjPublicLog('warn', 'watchdog error page failed', e);
      publicBootTimeline('[E6]', `watchdog error page: ${e && e.message}`);
      bootstrapDone = true;
      clearBootstrapTimeout();
      try {
        window.__SJ_PUBLIC_BOOTSTRAP__ = false;
      } catch (_) {}
      try {
        document.documentElement.classList.remove('sj-public-bootstrap-active');
        document.body.classList.remove('sj-public-bootstrap-active');
        document.body.classList.add('sj-public-ui-ready');
      } catch (_) {}
      triggerFullRecoveryOrReload('bootstrap-timeout-error-page-failed');
    }
  }, PUBLIC_BOOTSTRAP_TIMEOUT_MS);

  let showPage;
  try {
    showPage = await resolveShowPageWithTimeout(RESOLVE_SHOWPAGE_TIMEOUT_MS + 500);
  } catch (e) {
    logger.error('[PublicClient] showPage unavailable:', e);
    publicBootTimeline('[E6]', `showPage init: ${e && e.message}`);
    sjPublicLog('warn', 'showPage unavailable at bootstrap start', e);
    clearBootstrapTimeout();
    triggerFullRecoveryOrReload('bootstrap-no-showPage-initial');
    return;
  }

  try {
    updateBootProgress(15);

    if (hasPublicStaticTestParam()) {
      publicBootTimeline('[P6]', 'SKIP Firestore — sj_static_test=1');
      publicBootTimeline('[P7]', 'static shell (no doc)');
      stopPublicSiteListener();
      const merged = validateSettingsFields({
        companyName: 'Test static (fără Firestore)',
        campaignStatus: 'active',
        buttonText: 'Dacă vezi asta, UI merge',
        buttonColor: '#22c55e',
        totalTries: 1,
        remainingTries: 1,
        prizes: [],
        bgURLs: [],
        menuEnabled: false,
        communityEnabled: false
      });
      const migratedPrizes = migratePrizesToHaveIds(merged.prizes || []);
      setCurrentSettings({
        ...merged,
        prizes: migratedPrizes,
        offlinePlaysPending: 0
      });
      publicBootTimeline('[P8]', 'static config OK');
      setPublicBootstrapTitle(merged.companyName || 'Test');
      setPublicBootstrapLoadingHint('Se deschide…');
      try {
        window.__SJ_PUBLIC_MINIMAL_FIRST_PAINT = true;
        publicBootTimeline('[P9]', 'public UI render started (clientPage, minimal)');
        await showPage('clientPage');
        try {
          const analytics = await import('./client-analytics.js?v=82.86');
          analytics.trackClientInterfaceViewOnce();
        } catch (_) {
          /* ignore */
        }
      } catch (e) {
        publicBootTimeline('[E6]', String(e && e.message));
        throw e;
      } finally {
        try {
          window.__SJ_PUBLIC_MINIMAL_FIRST_PAINT = false;
        } catch (_) {}
      }
      finishBootstrap();
      sjPublicLog('info', 'static test boot OK');
      return;
    }

    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      publicBootTimeline('[E4]', 'offline at bootstrap start');
      sjPublicLog('warn', 'offline at bootstrap start');
      const errEl = document.getElementById('publicJoinErrorText');
      if (errEl) {
        errEl.textContent =
          'Fără conexiune la internet. După reconectare, pagina se poate reîncărca singură.';
      }
      await showPage('publicJoinErrorPage');
      finishBootstrap();
      return;
    }

    const ref = db.collection('publicSites').doc(slug);
    let snap = null;

    updateBootProgress(20);
    publicBootTimeline('[P6]', 'public doc fetch started (server-first)');
    if (typeof console !== 'undefined' && console.info) {
      console.info('[DATA]', 'fetch start —', slug);
    }
    try {
      snap = await fetchPublicSiteDoc(ref, slug);
      if (typeof console !== 'undefined' && console.info) {
        console.info('[DATA]', 'fetch success —', slug, snap && snap.viaRest ? '(REST)' : '(SDK)');
      }
    } catch (e) {
      {
        const e2 = e;
        publicBootTimeline('[E4]', `public fetch failed: ${e2 && e2.message}`);
        sjPublicLog('warn', 'fallback fetch failed', String(e2 && e2.message));
        if (typeof console !== 'undefined' && console.info) {
          console.info('[DATA]', 'fetch error —', String(e2 && e2.message));
        }
        try {
          if (sessionStorage.getItem('sj_data_recovery') !== '1') {
            sessionStorage.setItem('sj_data_recovery', '1');
            if (typeof console !== 'undefined' && console.info) {
              console.info('[DATA]', 'auto recovery triggered — clearing Firestore IDB + spressjoy cache');
            }
            if (typeof indexedDB !== 'undefined' && indexedDB.databases) {
              try {
                const dbs = await indexedDB.databases();
                await Promise.all(
                  dbs
                    .filter(function (d) { return d.name && d.name.toLowerCase().includes('firestore'); })
                    .map(function (d) {
                      return new Promise(function (res) {
                        try {
                          const req = indexedDB.deleteDatabase(d.name);
                          req.onsuccess = res;
                          req.onerror = res;
                          req.onblocked = res;
                        } catch (_) { res(); }
                      });
                    })
                );
              } catch (_) {}
            }
            if ('caches' in window) {
              try {
                const keys = await caches.keys();
                await Promise.all(
                  keys.map(function (k) {
                    return k.startsWith('spressjoy-') ? caches.delete(k) : Promise.resolve();
                  })
                );
              } catch (_) {}
            }
            if (typeof console !== 'undefined' && console.info) {
              console.info('[DATA]', 'auto recovery done — reloading');
            }
            try {
              var path = window.location.pathname || '/';
              window.location.replace(path + '?sj_data_recover=' + String(Date.now()));
            } catch (_) {}
            return;
          }
        } catch (_) {}
        throw e2;
      }
    }

    if (!snap || !snap.exists) {
      publicBootTimeline('[E4]', 'public doc missing');
      sjPublicLog('warn', 'public config missing', { slug });
      stopPublicSiteListener();
      const errEl = document.getElementById('publicJoinErrorText');
      if (errEl) {
        errEl.textContent =
          'Nu am putut încărca pagina locației. Link inexistent sau neconfigurat.';
      }
      await showPage('publicJoinErrorPage');
      finishBootstrap();
      return;
    }

    updateBootProgress(50);

    const raw = snap.data() || {};
    const strict = validatePublicSiteDocumentForClient(raw, slug);
    if (!strict.ok) {
      publicBootTimeline('[E5]', String(strict.reason || 'config invalid'));
      sjPublicLog('warn', 'public config invalid', strict.reason || '');
      stopPublicSiteListener();
      const errEl = document.getElementById('publicJoinErrorText');
      if (errEl) errEl.textContent = strict.reason || 'Config invalid.';
      await showPage('publicJoinErrorPage');
      finishBootstrap();
      return;
    }

    publicBootTimeline('[P8]', 'public config validated');

    const merged = validateSettingsFields({ ...raw });
    const migratedPrizes = migratePrizesToHaveIds(merged.prizes || []);

    setCurrentSettings({
      ...merged,
      prizes: migratedPrizes,
      offlinePlaysPending: 0
    });
    updateBootProgress(65);
    setPublicBootstrapTitle(String(merged.companyName || '').trim());
    // logo-ul pe ecranul de încărcare: îl așteptăm cel mult PUBLIC_LOGO_MAX_WAIT_MS (înainte până la 8 s + 0,5 s,
    // ceea ce, adunat cu citirea datelor pe o rețea lentă, depășea limita și apărea „Nu am putut încărca…”)
    await Promise.race([
      applyPublicBootstrapLogo(merged, { minVisibleMs: 300, slug }).catch(() => false),
      new Promise((r) => window.setTimeout(r, PUBLIC_LOGO_MAX_WAIT_MS))
    ]);

    try {
      updateBootProgress(75);
      window.__SJ_PUBLIC_MINIMAL_FIRST_PAINT = true;
      publicBootTimeline('[P9]', 'public UI render started (clientPage, minimal first paint)');
      await showPage('clientPage');
      applyClientPublicBrandLogo(merged);
      if (merged.internalDemo === true) mountPublicInternalDemoRibbon();
    } catch (renderErr) {
      publicBootTimeline('[E6]', `render failed: ${renderErr && renderErr.message}`);
      throw renderErr;
    } finally {
      try {
        window.__SJ_PUBLIC_MINIMAL_FIRST_PAINT = false;
      } catch (_) {}
    }

    updateBootProgress(100);
    finishBootstrap();

    try {
      const analytics = await import('./client-analytics.js?v=82.86');
      analytics.trackClientInterfaceViewOnce();
    } catch (_) {
      /* ignore */
    }

    schedulePublicPostBoot(slug, onStatusChange);

    logger.log('[PublicClient] Bootstrapped slug:', slug);
    sjPublicLog('info', 'bootstrapped slug OK', slug);
  } catch (err) {
    stopTrickle();
    logger.error('[PublicClient] Bootstrap failed:', err);
    publicBootTimeline('[E4]', `bootstrap catch: ${err && err.message}`);
    sjPublicLog('warn', 'bootstrap failed', err);
    const errEl = document.getElementById('publicJoinErrorText');
    if (errEl) {
      errEl.textContent =
        'Nu am putut încărca pagina locației. Verifică conexiunea și încearcă din nou.';
    }
    try {
      await showPage('publicJoinErrorPage');
    } catch (e2) {
      publicBootTimeline('[E6]', `error page: ${e2 && e2.message}`);
      sjPublicLog('warn', 'error page after bootstrap fail', e2);
      triggerFullRecoveryOrReload('bootstrap-catch-error-page');
      return;
    }
    finishBootstrap();
  }
}

export function clearPublicClientSlug() {
  activePublicSlug = null;
}
